import api from './api.service';

type ApiBody<T> = { success?: boolean; data?: T; message?: string };

async function unwrap<T>(promise: Promise<unknown>): Promise<T> {
  const body = (await promise) as ApiBody<T>;
  if (body?.data === undefined) throw new Error(body?.message || 'EPC subcontract admin request failed');
  return body.data;
}

function agreementParams(agreementId?: string) {
  return agreementId ? { params: { agreementId } } : {};
}

export type EpcSubcontractWorkspace = {
  agreement: Record<string, unknown>;
  obligations: Record<string, unknown>[];
  securities: Record<string, unknown>[];
  insurance: Record<string, unknown>[];
  paymentCertificates: Record<string, unknown>[];
  certifications: Record<string, unknown>[];
  variations: Record<string, unknown>[];
  claims: Record<string, unknown>[];
  forecast: EpcSubcontractForecast | null;
};

export type EpcSubcontractForecast = {
  agreementId: string;
  currency: string;
  contractSum: number;
  approvedVariations: number;
  certifiedToDate: number;
  appliedNotCertified: number;
  claimsOpen: number;
  retentionHeld: number;
  projectedFinal: number;
  ldExposureAdvisory?: number;
  ldExposure?: Record<string, unknown> | null;
  asOf: string;
};

export type EpcSubcontractControlTower = {
  agreementId: string;
  forecast: EpcSubcontractForecast;
  ldExposureAdvisory?: Record<string, unknown> | null;
  doubleCommitmentWarning?: {
    risk?: boolean;
    risks?: Record<string, unknown>[];
    linkedPurchaseOrders?: Record<string, unknown>[];
    policy?: string;
  } | null;
  overdueNotices: Record<string, unknown>[];
  counts: {
    paymentCertificates: number;
    certifications: number;
    variations: number;
    claims: number;
    eotRequests: number;
    finalAccounts: number;
    closeouts: number;
  };
  openVariations: Record<string, unknown>[];
  currency: string;
};

const base = '/epc/subcontract-admin';

export const epcSubcontractAdminService = {
  getRuntime: () => unwrap<Record<string, unknown>>(api.get(`${base}/runtime`)),

  listAgreements: (filters?: { projectId?: string; supplierId?: string; status?: string }) =>
    unwrap<Record<string, unknown>[]>(
      api.get(`${base}/agreements`, { params: filters || {} })
    ),
  getAgreement: (id: string) =>
    unwrap<Record<string, unknown>>(api.get(`${base}/agreements/${encodeURIComponent(id)}`)),
  createAgreement: (payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(api.post(`${base}/agreements`, payload)),
  createAgreementFromEst3: (payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(api.post(`${base}/agreements/from-est3`, payload)),
  patchAgreement: (id: string, payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(
      api.patch(`${base}/agreements/${encodeURIComponent(id)}`, payload)
    ),
  submitAgreement: (id: string) =>
    unwrap<Record<string, unknown>>(
      api.post(`${base}/agreements/${encodeURIComponent(id)}/submit`)
    ),

  getWorkspace: (agreementId: string) =>
    unwrap<EpcSubcontractWorkspace>(
      api.get(`${base}/agreements/${encodeURIComponent(agreementId)}/workspace`)
    ),
  getControlTower: (agreementId: string) =>
    unwrap<EpcSubcontractControlTower>(
      api.get(`${base}/agreements/${encodeURIComponent(agreementId)}/control-tower`)
    ),
  getForecast: (agreementId: string) =>
    unwrap<EpcSubcontractForecast>(
      api.get(`${base}/agreements/${encodeURIComponent(agreementId)}/forecast`)
    ),
  createForecastSnapshot: (agreementId: string) =>
    unwrap<Record<string, unknown>>(
      api.post(`${base}/agreements/${encodeURIComponent(agreementId)}/forecast-snapshots`)
    ),

  getCepProgressHints: (agreementId: string) =>
    unwrap<{
      available?: boolean;
      reason?: string;
      projectId?: string | null;
      hints?: Record<string, unknown>[];
      lines?: Record<string, unknown>[];
      note: string;
    }>(api.get(`${base}/agreements/${encodeURIComponent(agreementId)}/cep-progress-hints`)),

  getPaymentCertificateDraft: (agreementId: string, excludeCertificateId?: string) =>
    unwrap<{
      agreement: Record<string, unknown>;
      defaults: Record<string, unknown>;
      lines: Array<{
        agreementLineId: string;
        lineNumber?: number;
        lineCode?: string | null;
        description?: string | null;
        qty?: number | null;
        unitRate?: number | null;
        lineAmount: number;
        previousCertifiedAmount: number;
        remainingAmount: number;
        costCategory?: string;
        wbsNodeId?: string | null;
      }>;
    }>(
      api.get(
        `${base}/agreements/${encodeURIComponent(agreementId)}/payment-certificate-draft`,
        {
          params: excludeCertificateId
            ? { excludeCertificateId }
            : undefined,
        }
      )
    ),

  getLdExposure: (agreementId: string) =>
    unwrap<Record<string, unknown>>(
      api.get(`${base}/agreements/${encodeURIComponent(agreementId)}/ld-exposure`)
    ),

  getCommitmentRisk: (agreementId: string) =>
    unwrap<Record<string, unknown>>(
      api.get(`${base}/agreements/${encodeURIComponent(agreementId)}/commitment-risk`)
    ),

  getSiteMaterialEvidence: (agreementId: string) =>
    unwrap<{
      agreement_id: string;
      project_id: string | null;
      items: Array<{
        item_id: string;
        item_code?: string | null;
        item_name?: string | null;
        quantity: number;
        suggested_free_issue_amount: number;
        supply_kind?: string;
      }>;
      suggested_free_issue_excluded_amount: number;
      suggested_materials_on_site_amount: number;
      note?: string;
    }>(api.get(`${base}/agreements/${encodeURIComponent(agreementId)}/site-material-evidence`)),

  listObligations: (agreementId: string) =>
    unwrap<Record<string, unknown>[]>(
      api.get(`${base}/agreements/${encodeURIComponent(agreementId)}/obligations`)
    ),
  createObligation: (agreementId: string, payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(
      api.post(`${base}/agreements/${encodeURIComponent(agreementId)}/obligations`, payload)
    ),
  patchObligation: (id: string, payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(
      api.patch(`${base}/obligations/${encodeURIComponent(id)}`, payload)
    ),

  listSecurities: (agreementId: string) =>
    unwrap<Record<string, unknown>[]>(
      api.get(`${base}/agreements/${encodeURIComponent(agreementId)}/securities`)
    ),
  createSecurity: (agreementId: string, payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(
      api.post(`${base}/agreements/${encodeURIComponent(agreementId)}/securities`, payload)
    ),
  patchSecurity: (id: string, payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(
      api.patch(`${base}/securities/${encodeURIComponent(id)}`, payload)
    ),

  listInsurance: (agreementId: string) =>
    unwrap<Record<string, unknown>[]>(
      api.get(`${base}/agreements/${encodeURIComponent(agreementId)}/insurance`)
    ),
  createInsurance: (agreementId: string, payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(
      api.post(`${base}/agreements/${encodeURIComponent(agreementId)}/insurance`, payload)
    ),
  patchInsurance: (id: string, payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(
      api.patch(`${base}/insurance/${encodeURIComponent(id)}`, payload)
    ),

  listNotices: (agreementId?: string) =>
    unwrap<Record<string, unknown>[]>(api.get(`${base}/notices`, agreementParams(agreementId))),
  getNotice: (id: string) =>
    unwrap<Record<string, unknown>>(api.get(`${base}/notices/${encodeURIComponent(id)}`)),
  createNotice: (payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(api.post(`${base}/notices`, payload)),
  patchNotice: (id: string, payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(
      api.patch(`${base}/notices/${encodeURIComponent(id)}`, payload)
    ),
  issueNotice: (id: string) =>
    unwrap<Record<string, unknown>>(api.post(`${base}/notices/${encodeURIComponent(id)}/issue`)),

  listPaymentCertificates: (agreementId?: string) =>
    unwrap<Record<string, unknown>[]>(
      api.get(`${base}/payment-certificates`, agreementParams(agreementId))
    ),
  getPaymentCertificate: (id: string) =>
    unwrap<Record<string, unknown>>(
      api.get(`${base}/payment-certificates/${encodeURIComponent(id)}`)
    ),
  createPaymentCertificate: (payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(api.post(`${base}/payment-certificates`, payload)),
  patchPaymentCertificate: (id: string, payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(
      api.patch(`${base}/payment-certificates/${encodeURIComponent(id)}`, payload)
    ),
  submitPaymentCertificate: (id: string) =>
    unwrap<Record<string, unknown>>(
      api.post(`${base}/payment-certificates/${encodeURIComponent(id)}/submit`)
    ),

  listCertifications: (agreementId?: string) =>
    unwrap<Record<string, unknown>[]>(
      api.get(`${base}/certifications`, agreementParams(agreementId))
    ),
  getCertification: (id: string) =>
    unwrap<Record<string, unknown>>(
      api.get(`${base}/certifications/${encodeURIComponent(id)}`)
    ),
  createCertification: (payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(api.post(`${base}/certifications`, payload)),
  confirmCertification: (id: string) =>
    unwrap<Record<string, unknown>>(
      api.post(`${base}/certifications/${encodeURIComponent(id)}/confirm`)
    ),
  convertCertificationToSupplierInvoice: (
    id: string,
    payload?: {
      departmentId?: string | null;
      expenseAccountId?: string | null;
      vatConfigurationId?: string | null;
      inputVatAccountId?: string | null;
      applyVat?: boolean;
    }
  ) =>
    unwrap<Record<string, unknown>>(
      api.post(
        `${base}/certifications/${encodeURIComponent(id)}/convert-to-supplier-invoice`,
        payload || {}
      )
    ),

  listChangeEvents: (agreementId?: string) =>
    unwrap<Record<string, unknown>[]>(
      api.get(`${base}/change-events`, agreementParams(agreementId))
    ),
  createChangeEvent: (payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(api.post(`${base}/change-events`, payload)),
  patchChangeEvent: (id: string, payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(
      api.patch(`${base}/change-events/${encodeURIComponent(id)}`, payload)
    ),

  listVariations: (agreementId?: string) =>
    unwrap<Record<string, unknown>[]>(api.get(`${base}/variations`, agreementParams(agreementId))),
  getVariation: (id: string) =>
    unwrap<Record<string, unknown>>(api.get(`${base}/variations/${encodeURIComponent(id)}`)),
  createVariation: (payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(api.post(`${base}/variations`, payload)),
  patchVariation: (id: string, payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(
      api.patch(`${base}/variations/${encodeURIComponent(id)}`, payload)
    ),
  submitVariation: (id: string) =>
    unwrap<Record<string, unknown>>(
      api.post(`${base}/variations/${encodeURIComponent(id)}/submit`)
    ),

  listClaims: (agreementId?: string) =>
    unwrap<Record<string, unknown>[]>(api.get(`${base}/claims`, agreementParams(agreementId))),
  getClaim: (id: string) =>
    unwrap<Record<string, unknown>>(api.get(`${base}/claims/${encodeURIComponent(id)}`)),
  createClaim: (payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(api.post(`${base}/claims`, payload)),
  patchClaim: (id: string, payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(api.patch(`${base}/claims/${encodeURIComponent(id)}`, payload)),
  submitClaim: (id: string) =>
    unwrap<Record<string, unknown>>(api.post(`${base}/claims/${encodeURIComponent(id)}/submit`)),

  listEotRequests: (agreementId?: string) =>
    unwrap<Record<string, unknown>[]>(
      api.get(`${base}/eot-requests`, agreementParams(agreementId))
    ),
  getEotRequest: (id: string) =>
    unwrap<Record<string, unknown>>(api.get(`${base}/eot-requests/${encodeURIComponent(id)}`)),
  createEotRequest: (payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(api.post(`${base}/eot-requests`, payload)),
  patchEotRequest: (id: string, payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(
      api.patch(`${base}/eot-requests/${encodeURIComponent(id)}`, payload)
    ),
  submitEotRequest: (id: string) =>
    unwrap<Record<string, unknown>>(
      api.post(`${base}/eot-requests/${encodeURIComponent(id)}/submit`)
    ),

  listFinalAccounts: (agreementId?: string) =>
    unwrap<Record<string, unknown>[]>(
      api.get(`${base}/final-accounts`, agreementParams(agreementId))
    ),
  getFinalAccount: (id: string) =>
    unwrap<Record<string, unknown>>(
      api.get(`${base}/final-accounts/${encodeURIComponent(id)}`)
    ),
  createFinalAccount: (payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(api.post(`${base}/final-accounts`, payload)),
  submitFinalAccount: (id: string) =>
    unwrap<Record<string, unknown>>(
      api.post(`${base}/final-accounts/${encodeURIComponent(id)}/submit`)
    ),

  listCloseouts: (agreementId?: string) =>
    unwrap<Record<string, unknown>[]>(api.get(`${base}/closeouts`, agreementParams(agreementId))),
  getCloseout: (id: string) =>
    unwrap<Record<string, unknown>>(api.get(`${base}/closeouts/${encodeURIComponent(id)}`)),
  createCloseout: (payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(api.post(`${base}/closeouts`, payload)),
  patchCloseout: (id: string, payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(
      api.patch(`${base}/closeouts/${encodeURIComponent(id)}`, payload)
    ),
  submitCloseout: (id: string) =>
    unwrap<Record<string, unknown>>(
      api.post(`${base}/closeouts/${encodeURIComponent(id)}/submit`)
    ),
};
